/* eslint-disable no-constant-condition */
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Tooltip } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Oval } from 'react-loader-spinner';
import { useSelector } from 'react-redux';

import { useAppDispatch } from 'store/store';
import {
	addChangesHistory,
	deleteTestCases,
	updateUserStoryTests,
} from 'store/ticket-service/asyncActions';
import { getActiveTicket } from 'store/ticket-service/selector';
import {
	setEditMode,
	setIsTicketUpdate,
	updateLocalTicket,
} from 'store/ticket-service/ticketSlice';
import { setIsOpenNoDeleteAccess, triggerTicketSave } from 'store/user-service/userSlice';

import { flexMixin } from 'utils/flexMixin';
import { normalizeObjectKeys } from 'utils/normalizeObjectKeys';

import {
	HistoryActionTypes,
	NamespacesList,
	SharePermissions,
	TicketStages,
} from 'core/enums/common';
import { editTestCasesSchema, editTitleSchema } from 'core/helpers/yupSchemas';

import { ExpandableTextField } from 'components/shared/ExpandableTextField';

import { ReactComponent as Refresh } from 'assets/icons/refresh.svg';

import * as Styled from '../UserStoryTests/styled';
import {
	BackendUserStoryTest,
	EditTestCasesFormProps,
	FormUserStoryTest,
	ITestPlan,
	IUserStoryTest,
} from '../types';

import { TestPlanDropdownHeader } from './DropdownHeader';
import { ListElement } from './ListElement';

export const EditTestCasesStep: FC<EditTestCasesFormProps> = ({
	allUserStoryTests,
	currentUserStoryTest,
	setUserStoryTests,
	pageIndex,
	ticketTitle,
	setTitleError,
}) => {
	const { t: commonTranslate } = useTranslation(NamespacesList['common']);
	const { t: testCasesTranslate } = useTranslation(NamespacesList['testCasesStep']);

	const dispatch = useAppDispatch();

	const activeTicket = useSelector(getActiveTicket);

	const [editTestCaseLoading, setEditTestCaseLoading] = useState(true);
	const [updatingLoading, setUpdatingLoading] = useState(false);

	const {
		control,
		handleSubmit,
		register,
		reset,
		formState: { errors },
		setValue,
		getValues,
	} = useForm({
		// @ts-ignore
		resolver: yupResolver(editTestCasesSchema()),
		defaultValues: {
			userStoryTests: [],
		},
	});

	const { fields: storyTests } = useFieldArray({
		control,
		name: 'userStoryTests',
	});

	const handleResetEditForm = useCallback(() => {
		dispatch(setEditMode(false));
	}, []);

	const handleGenerateEditForm = async () => {
		if (allUserStoryTests) {
			const updatedUserStoryTests = [currentUserStoryTest]
				.filter((test) => test)
				.map((tests) => ({
					...tests,
					testScenarios: tests.testScenarios.map((item) => ({
						data: item,
						isDeleted: false,
					})),
					testCases: tests.testCases.map((item) => ({
						...item,
						isDeleted: false,
						testSteps: item.testSteps.map((step) => ({
							data: step,
							isDeleted: false,
						})),
					})),
				}));

			reset({
				userStoryTests: updatedUserStoryTests,
			});
		}
	};

	const currentStepPermission = useMemo(() => {
		const currentStagePermission = activeTicket?.permissions?.find(
			(permission) =>
				permission.ticketCurrentStep === TicketStages.testCases ||
				permission.ticketCurrentStep === null
		);

		if (currentStagePermission) return currentStagePermission.permission;

		return null;
	}, [activeTicket?.id, activeTicket?.permissions]);

	const saveTicketChanges = () => {
		dispatch(setIsTicketUpdate(true));
		dispatch(triggerTicketSave());
		dispatch(setEditMode(false));
	};

	const getDeletedTestCases = (userStoryTests: Array<FormUserStoryTest>) => {
		const deletedTestCasesId: Array<string> = [];

		userStoryTests.forEach((item) => {
			item.testCases.forEach((testCase) => {
				if (testCase.isDeleted) deletedTestCasesId.push(testCase?.id || '');
			});
		});

		return deletedTestCasesId;
	};

	const handleTicketChanges = ({
		oldData,
		newData,
	}: {
		oldData: BackendUserStoryTest[];
		newData: BackendUserStoryTest[];
	}) => {
		if (activeTicket) {
			dispatch(
				addChangesHistory({
					userId: JSON.parse(localStorage.getItem('user') || '{}')?.id || '',
					ticketId: activeTicket.id,
					ticketStep: TicketStages.general,
					actionType: HistoryActionTypes.testCasesEdit,
					oldValue: JSON.stringify(oldData),
					newValue: JSON.stringify(newData),
				})
			);
		}
	};

	const formatUserStoryTest = (userStoryTest: FormUserStoryTest): IUserStoryTest => ({
		...userStoryTest,
		testScenarios: userStoryTest.testScenarios
			.filter((scenario: any) => !scenario.isDeleted)
			.map((scenario) => scenario.data),
		testCases: userStoryTest.testCases
			.filter((testCase) => !testCase.isDeleted)
			.map((testCase) => ({
				...testCase,
				testSteps: testCase.testSteps
					.filter((scenario: any) => !scenario.isDeleted)
					.map((scenario) => scenario.data),
			})),
	});

	const getReducedString = (array: Array<string>) =>
		array.reduce((accumulator: string, value: string) => {
			const splitString = value.split('');

			return accumulator + value + (splitString[splitString.length - 1] === '.' ? ' ' : '. ');
		}, '');

	const userStoriesTestsToBackEndData = (userStoryTests: Array<IUserStoryTest>) =>
		userStoryTests.map((userStoryTest: IUserStoryTest) => ({
			...userStoryTest,
			testScenarios: getReducedString(userStoryTest.testScenarios),
			testCases: userStoryTest.testCases.map((testCase) => ({
				...testCase,
				testSteps: getReducedString(testCase.testSteps),
			})),
		}));

	const updateUserStoryTestInfo = (data: { userStoryTests: Array<FormUserStoryTest> }) => {
		const { userStoryTests } = data;

		const updatedUserStoryTests: Array<IUserStoryTest> = allUserStoryTests.map((item, index) =>
			index + 1 === pageIndex ? formatUserStoryTest(userStoryTests[0]) : item
		);

		setUserStoryTests(updatedUserStoryTests);

		const backEndUserStoryTests: Array<BackendUserStoryTest> =
			userStoriesTestsToBackEndData(updatedUserStoryTests);

		editTitleSchema()
			.validate({ title: ticketTitle })
			.then(async (res) => {
				if (res) {
					if (activeTicket) {
						const id = activeTicket.id;
						try {
							setUpdatingLoading(true);

							const deletedTestCases = getDeletedTestCases(userStoryTests);

							await dispatch(updateUserStoryTests({ testCase: backEndUserStoryTests })).unwrap();

							if (deletedTestCases.length > 0)
								await dispatch(deleteTestCases(deletedTestCases)).unwrap();

							dispatch(
								updateLocalTicket({
									id,
									title: ticketTitle,
									userStoryTests: updatedUserStoryTests,
								})
							);

							setUpdatingLoading(false);
						} catch (error) {
							console.log(error);
						}
						handleTicketChanges({
							oldData: userStoriesTestsToBackEndData(allUserStoryTests),
							newData: backEndUserStoryTests,
						});
					}
					saveTicketChanges();
				}
			})
			.catch((error) => {
				setTitleError?.('title', {
					type: 'manual',
					message: error.message,
				});
			});
	};

	useEffect(() => {
		// Due to the fast rendering, the UI blinking, so a loader was added to avoid this
		setTimeout(() => {
			setEditTestCaseLoading(false);
		}, 1000);
		handleGenerateEditForm();
	}, []);

	const handleCancelOption = (path: any, data: any) => {
		if (currentStepPermission === null || currentStepPermission === SharePermissions.Manage) {
			setValue(path, data);
			reset({
				...getValues(),
			});
		} else {
			dispatch(setIsOpenNoDeleteAccess(true));
		}
	};

	return (
		<form
			style={{
				width: '100%',
				height: '100%',
				display: 'grid',
				gridTemplateRows: 'auto 0fr',
				overflow: 'auto',
			}}
			onSubmit={handleSubmit(updateUserStoryTestInfo)}
		>
			<Styled.UserStoryTests
				sx={{
					height: '100%',
					boxSizing: 'border-box',
					overflow: 'auto',
				}}
			>
				{!editTestCaseLoading ? (
					storyTests?.map((test, storyTestIndex) => (
						<Styled.UserStoryTest key={`${storyTestIndex}.${test.id}`}>
							<TestPlanDropdownHeader
								isDropped={storyTestIndex === 0}
								header={
									<Styled.TestPlanTitle
										sx={{ ...flexMixin({ justify: 'flex-start', gap: 15 }), width: '100%' }}
									>
										<span>{testCasesTranslate('testPlan.title')}:</span>
										{normalizeObjectKeys(test)['usecasetitle']}
									</Styled.TestPlanTitle>
								}
							>
								{test && (
									<>
										{(
											Object.entries(test.testPlan) as Array<
												[keyof ITestPlan, string | Array<string>]
											>
										).map(([key, value], testPlanIndex) =>
											key === 'id' ? null : (
												<Box key={`${testPlanIndex}.${key}`} sx={{ width: '100%' }}>
													<Styled.TestPlanSubTitle>
														{testCasesTranslate(`testPlan.${key}`)}:
													</Styled.TestPlanSubTitle>
													<ExpandableTextField
														errors={errors.userStoryTests?.[storyTestIndex]?.testPlan?.[key]}
														registerConfig={register(
															`userStoryTests.${storyTestIndex}.testPlan.${key}`
														)}
														isDisabled={false}
														defaultValue={typeof value === 'string' ? value : value[0]}
													/>
												</Box>
											)
										)}
										<Box sx={{ width: '100%' }}>
											<Styled.TestPlanSubTitle>
												{testCasesTranslate(`testScenarios.title`)}:
											</Styled.TestPlanSubTitle>
											<ol style={{ margin: '0', boxSizing: 'border-box' }}>
												{test.testScenarios.map((scenario, scenarioIndex) => (
													<ListElement
														countOfElements={test.testScenarios.length - 1}
														key={`${scenarioIndex}.${scenario.data}`}
														index={scenarioIndex}
														onClick={() =>
															handleCancelOption(
																`userStoryTests.${storyTestIndex}.testScenarios.${scenarioIndex}.isDeleted`,
																!scenario.isDeleted
															)
														}
														isDeleted={scenario.isDeleted}
													>
														<ExpandableTextField
															errors={
																errors.userStoryTests?.[storyTestIndex]?.testScenarios?.[
																	scenarioIndex
																]?.data
															}
															registerConfig={register(
																`userStoryTests.${storyTestIndex}.testScenarios.${scenarioIndex}.data`
															)}
															defaultValue={scenario.data}
															isDisabled={scenario.isDeleted}
														/>
													</ListElement>
												))}
											</ol>
										</Box>
										<Box sx={{ width: '100%' }}>
											<Styled.TestPlanSubTitle>
												{testCasesTranslate(`testCases.title`)}:
											</Styled.TestPlanSubTitle>
											<ol style={{ margin: '0', boxSizing: 'border-box' }}>
												{test.testCases.map((testCase, testCaseIndex) => (
													<li
														key={`${testCaseIndex}.${testCase.testCaseId}`}
														style={{ marginBottom: '13px' }}
													>
														<Box sx={{ ...flexMixin({ justify: 'flex-end' }) }}>
															<Tooltip
																className="not-rtl-able"
																componentsProps={{
																	tooltip: {
																		sx: {
																			backgroundColor: '#f5f5f9',
																			color: 'rgba(0, 0, 0, 0.87)',
																			fontSize: '12px',
																			maxWidth: 200,
																			textAlign: 'center',
																			marginBottom: '5px',
																			border: '1px solid #dadde9',
																			'& .MuiTooltip-popper': { margin: 0 },
																		},
																	},
																	popper: {
																		sx: {
																			marginBottom: '5px',
																		},
																	},
																}}
																PopperProps={{
																	modifiers: [
																		{
																			name: 'offset',
																			options: {
																				offset: [0, 0],
																			},
																		},
																	],
																}}
																title={
																	test.testCases.length - 1 === 0
																		? commonTranslate('tooltips.atLeastOneExcitingElement')
																		: null
																}
																placement="left"
															>
																<Box>
																	<Button
																		type="button"
																		sx={{
																			minWidth: '24px',
																			height: '24px',
																			fontSize: '24px',
																			color: '#9FA6B3',
																		}}
																		onClick={() =>
																			handleCancelOption(
																				`userStoryTests.${storyTestIndex}.testCases.${testCaseIndex}.isDeleted`,
																				!testCase.isDeleted
																			)
																		}
																	>
																		{testCase.isDeleted ? <Refresh /> : <span>&times;</span>}
																	</Button>
																</Box>
															</Tooltip>
														</Box>

														<Styled.TestCaseBox
															sx={{
																opacity: testCase.isDeleted ? '0.5' : '1',
																pointerEvents: testCase.isDeleted ? 'none' : 'auto',
															}}
														>
															<Styled.TestPlanSubTitle
																sx={{
																	...flexMixin({ justify: 'flex-start', gap: 15 }),
																	width: '100%',
																}}
															>
																{testCase.testCaseId}:
																<ExpandableTextField
																	errors={
																		errors.userStoryTests?.[storyTestIndex]?.testCases?.[
																			testCaseIndex
																		]?.description
																	}
																	registerConfig={register(
																		`userStoryTests.${storyTestIndex}.testCases.${testCaseIndex}.description`
																	)}
																	defaultValue={testCase.description}
																	isDisabled={false}
																/>
															</Styled.TestPlanSubTitle>
															<Styled.TestDescription
																sx={{
																	...flexMixin({ justify: 'flex-start', gap: 15 }),
																	width: '100%',
																}}
															>
																<span>{testCasesTranslate('testCases.preConditions')}:</span>{' '}
																<ExpandableTextField
																	errors={
																		errors.userStoryTests?.[storyTestIndex]?.testCases?.[
																			testCaseIndex
																		]?.preConditions
																	}
																	registerConfig={register(
																		`userStoryTests.${storyTestIndex}.testCases.${testCaseIndex}.preConditions`
																	)}
																	defaultValue={testCase.preConditions}
																	isDisabled={false}
																/>
															</Styled.TestDescription>
															<Styled.TestDescription>
																<span>{testCasesTranslate('testCases.testSteps')}:</span>
															</Styled.TestDescription>
															<ol style={{ width: '100%', boxSizing: 'border-box' }}>
																{testCase.testSteps.map((step, stepIndex) => (
																	<ListElement
																		countOfElements={testCase.testSteps.length - 1}
																		key={`${stepIndex}.${step.data}`}
																		index={stepIndex}
																		onClick={() =>
																			handleCancelOption(
																				`userStoryTests.${storyTestIndex}.testCases.${testCaseIndex}.testSteps.${stepIndex}.isDeleted`,
																				!step.isDeleted
																			)
																		}
																		isDeleted={step.isDeleted}
																	>
																		<ExpandableTextField
																			errors={
																				errors.userStoryTests?.[storyTestIndex]?.testCases?.[
																					testCaseIndex
																				]?.testSteps?.[stepIndex]?.data
																			}
																			registerConfig={register(
																				`userStoryTests.${storyTestIndex}.testCases.${testCaseIndex}.testSteps.${stepIndex}.data`
																			)}
																			defaultValue={step.data}
																			isDisabled={step.isDeleted}
																		/>
																	</ListElement>
																))}
															</ol>
															<Styled.TestDescription
																sx={{
																	...flexMixin({ align: 'flex-start', direction: 'column' }),
																	width: '100%',
																}}
															>
																<span>{testCasesTranslate('testCases.testData')}:</span>
																<ExpandableTextField
																	errors={
																		errors.userStoryTests?.[storyTestIndex]?.testCases?.[
																			testCaseIndex
																		]?.testData
																	}
																	registerConfig={register(
																		`userStoryTests.${storyTestIndex}.testCases.${testCaseIndex}.testData`
																	)}
																	defaultValue={testCase.testData}
																	isDisabled={false}
																/>
															</Styled.TestDescription>
															<Styled.TestDescription
																sx={{
																	...flexMixin({ align: 'flex-start', direction: 'column' }),
																	width: '100%',
																}}
															>
																<span>{testCasesTranslate('testCases.expectedResult')}:</span>{' '}
																<ExpandableTextField
																	errors={
																		errors.userStoryTests?.[storyTestIndex]?.testCases?.[
																			testCaseIndex
																		]?.expectedResult
																	}
																	registerConfig={register(
																		`userStoryTests.${storyTestIndex}.testCases.${testCaseIndex}.expectedResult`
																	)}
																	defaultValue={testCase.expectedResult}
																	isDisabled={false}
																/>
															</Styled.TestDescription>
														</Styled.TestCaseBox>
													</li>
												))}
											</ol>
										</Box>
									</>
								)}
							</TestPlanDropdownHeader>
						</Styled.UserStoryTest>
					))
				) : (
					<Styled.LoaderBox>
						<Oval
							height={28}
							width={28}
							color="#fff"
							wrapperStyle={{}}
							wrapperClass=""
							visible={true}
							ariaLabel="oval-loading"
							secondaryColor="#91B6FF"
							strokeWidth={5}
							strokeWidthSecondary={5}
						/>
					</Styled.LoaderBox>
				)}
			</Styled.UserStoryTests>
			<Box
				className="not-rtl-able"
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					marginTop: 'auto',
				}}
			>
				<Button
					sx={{
						marginTop: '16px',
						height: '44px',
						width: '133px',
						borderRadius: '8px',
						textTransform: 'none',
						fontSize: '16px',
						fontWeight: '700',
					}}
					variant="outlined"
					color="primary"
					onClick={handleResetEditForm}
				>
					{commonTranslate('buttons.cancel')}
				</Button>

				{updatingLoading ? (
					<Button
						sx={{
							marginTop: '16px',
							height: '44px',
							width: '133px',
							borderRadius: '8px',
						}}
						type="submit"
						variant="contained"
						color="primary"
						disabled
					>
						<Oval
							height={24}
							width={24}
							color="rgba(0, 0, 0, 0.12)"
							wrapperStyle={{}}
							wrapperClass=""
							visible={true}
							ariaLabel="oval-loading"
							secondaryColor="rgba(0, 0, 0, 0.12)"
							strokeWidth={5}
							strokeWidthSecondary={5}
						/>
					</Button>
				) : (
					<Button
						sx={{
							marginTop: '16px',
							height: '44px',
							width: '133px',
							borderRadius: '8px',
							textTransform: 'none',
							fontSize: '16px',
							fontWeight: '700',
						}}
						type="submit"
						variant="contained"
						color="primary"
					>
						{commonTranslate('buttons.save')}
					</Button>
				)}
			</Box>
		</form>
	);
};
